import { useCallback, useMemo, useState } from 'react'
import {
  AlertIcon,
  CheckIcon,
  ChildrenIcon,
  DashButton,
  DataSurface,
  Field,
  InfoIcon,
  Input,
  Note,
  Panel,
  PointIcon,
  SchoolIcon,
  Select,
  StatusPill,
} from '../../../shared/components'
import { useResource } from '../../../shared/hooks/useResource'
import { useSeededQuery } from '../../../shared/hooks/useSeededQuery'
import {
  getAllStudents,
  getClasses,
  type ClassSummary,
  type StudentRow,
} from '../../../shared/api/classes'
import { addStudent, type AddedStudent } from '../../../shared/api/student'
import type { StaffAuth } from '../../../shared/api/teaching'
import type { Lang } from './copy'
import { ADMIN_COPY, BAND_LABEL, describeAdminError } from './adminCopy'
import {
  activityState,
  canAddStudent,
  classOptions,
  enrollableClasses,
  filterStudents,
  resolveClassFilter,
  rollNumberClash,
  studentsToCsv,
  summariseStudents,
  type ActivityFilter,
  type ActivityState,
} from './studentsModel'
import {
  ActiveFilters,
  FilterAction,
  FilterSearch,
  FilterSelect,
  NoMatches,
  downloadCsv,
  type FilterChip,
} from './FilterKit'

const ACTIVITY_OPTIONS: readonly { value: ActivityFilter; label: string }[] = [
  { value: 'ALL', label: 'All activity' },
  { value: 'RECENT', label: 'Played recently' },
  { value: 'QUIET', label: 'Quiet (7+ days)' },
  { value: 'NEVER', label: 'Never played' },
]

/**
 * E7 — every child in the school, and the way into their report.
 *
 * 🔴 WHY THIS SCREEN DID NOT EXIST, WHICH IS THE SAME ANSWER E3 GAVE. The
 * endpoint was built, scoped and tested and nothing in this tier called it:
 * `GET /students` is `@Roles('TEACHER','COORDINATOR','ADMIN','PRINCIPAL',
 * 'SUPER_ADMIN')` and `ScopeService.classScope` returns `{ schoolId }` for the
 * two school roles, so a head teacher has been able to read every child in their
 * school since §8.1 — from a screen that was only ever drawn for a teacher. The
 * school tier could show you how many children there are (E1's tile) and not
 * one of their names.
 *
 * 🔴 ONE REQUEST FOR THE WHOLE SCREEN. The obvious build is one roster call per
 * class, which is a round trip per section and grows with the timetable —
 * `architecture.md` §12's *"25 queries for one dashboard"* moved into the
 * browser. This is the same single scoped query the teacher tier makes, and at
 * this tier it returns the school.
 *
 * ⚠️ EVERY *READING* CONTROL ON IT CHANGES WHAT IS DRAWN RATHER THAN WHAT IS
 * TRUE — the roll number, the class and the age band are all edited on E4, and
 * duplicating those here would be a second screen for one fact. D4.
 *
 * ✅ THE ONE WRITE IT NOW CARRIES IS **ENROLLING A CHILD**, AND IT IS HERE
 * BECAUSE IT EXISTED NOWHERE ELSE IN THIS TIER. The paragraph above used to end
 * *"no control at all"*, which was right about editing and wrong about adding:
 * a school's only routes into its own roster were `POST /roster/import` — a
 * spreadsheet, ADMIN-only against threat T8 — and the TEACHER tier's form. A
 * head teacher enrolling one child mid-term had to re-export the school or ask a
 * class teacher to type a name. `POST /players` has accepted an ADMIN since it
 * shipped (`mayAccessClass(classId, 'roster')`); nothing in this tier called it.
 *
 * ⚠️ S6 STILL MAKES A PRINCIPAL READ-ONLY. `canWrite` decides whether the form
 * is DRAWN — the server refuses them regardless.
 */

export interface SchoolStudentsScreenProps {
  lang?: Lang
  auth?: StaffAuth
  /** Opens E8. The row travels with it — see `SchoolStudentReport`. */
  onOpenChild: (student: StudentRow) => void
  /**
   * 🔴 S6 — A PRINCIPAL READS THIS SCREEN AND ENROLS NOBODY.
   *
   * `ScopeService.mayAccessClass` gives them `read` and refuses every other
   * level, so `POST /players` answers them 403. The form is not drawn at all
   * rather than drawn and refused — D4's rule, and `TeachersScreen`'s.
   */
  canWrite?: boolean
}

export function SchoolStudentsScreen({
  lang = 'en',
  auth,
  onOpenChild,
  canWrite = true,
}: SchoolStudentsScreenProps) {
  const t = ADMIN_COPY[lang]

  /* Seeded from `?q=` so the chrome's search can land on one child. */
  const [query, setQuery] = useSeededQuery()
  const [classId, setClassId] = useState<string | null>(null)
  const [activity, setActivity] = useState<ActivityFilter>('ALL')
  const [page, setPage] = useState(1)
  const [pageSize, setPageSize] = useState(10)
  const [showAddForm, setShowAddForm] = useState(false)

  const resource = useResource<StudentRow[]>(
    (signal) => getAllStudents({ ...auth, signal }),
    ['school-students'],
    { isEmpty: (rows) => rows.length === 0 },
  )
  /*
   * 🔴 THE CLASS LIST IS ITS OWN READ, NOT `classOptions(rows)`. That one is
   * derived from the children on screen, so a class nobody is enrolled in yet
   * does not appear in it — and a class with no children is precisely the one an
   * admin is most likely to be filling. The filter above may keep using it: a
   * filter for an empty class would filter to nothing.
   */
  const classList = useResource<ClassSummary[]>(
    (signal) => getClasses({ ...auth, signal }),
    ['school-classes'],
  )

  const rows = useMemo(() => resource.data ?? [], [resource.data])
  const now = useMemo(() => resource.lastLoadedAt ?? new Date(), [resource.lastLoadedAt])

  const totals = useMemo(() => summariseStudents(rows, now), [rows, now])
  const classes = useMemo(() => classOptions(rows), [rows])
  /*
   * ⚠️ THE CONTROL AND THE TABLE READ THE SAME VALUE, and `studentsModel`
   * says why: a `<select>` silently displays its first option when the selected
   * one is no longer in the list, so a filter for a class that has emptied would
   * hide every child under a control reading "All Classes".
   */
  const selectedClassId = useMemo(
    () => resolveClassFilter(classes, classId),
    [classes, classId],
  )
  const shown = useMemo(
    () => filterStudents(rows, { query, classId: selectedClassId, activity }, now),
    [rows, query, selectedClassId, activity, now],
  )

  const totalPages = Math.max(1, Math.ceil(shown.length / pageSize))
  const paginatedRows = useMemo(() => {
    const start = (page - 1) * pageSize
    return shown.slice(start, start + pageSize)
  }, [shown, page, pageSize])

  const reload = useCallback(() => {
    resource.retry()
    classList.retry()
  }, [resource, classList])

  const download = useCallback(() => {
    downloadCsv(studentsToCsv(shown), 'school-children.csv')
  }, [shown])

  const clearFilters = useCallback(() => {
    setQuery('')
    setClassId(null)
    setActivity('ALL')
    setPage(1)
  }, [setQuery])

  const chips = useMemo<FilterChip[]>(() => {
    const out: FilterChip[] = []
    if (query.trim() !== '')
      out.push({ key: 'q', label: `“${query.trim()}”`, onRemove: () => { setQuery(''); setPage(1) } })
    if (selectedClassId !== null)
      out.push({
        key: 'class',
        label: classes.find((c) => c.classId === selectedClassId)?.label ?? 'Class',
        onRemove: () => { setClassId(null); setPage(1) },
      })
    if (activity !== 'ALL')
      out.push({
        key: 'activity',
        label: ACTIVITY_OPTIONS.find((o) => o.value === activity)?.label ?? activity,
        onRemove: () => { setActivity('ALL'); setPage(1) },
      })
    return out
  }, [query, selectedClassId, activity, classes, setQuery])

  const getInitials = (name: string) => {
    const parts = name.trim().split(' ')
    if (parts.length >= 2) return (parts[0][0] + parts[1][0]).toUpperCase()
    return name.substring(0, 2).toUpperCase()
  }

  const getBadgeColor = (index: number) => {
    const colors = [
      'bg-indigo-100 text-indigo-700 border-indigo-200',
      'bg-emerald-100 text-emerald-700 border-emerald-200',
      'bg-blue-100 text-blue-700 border-blue-200',
      'bg-amber-100 text-amber-700 border-amber-200',
      'bg-purple-100 text-purple-700 border-purple-200',
    ]
    return colors[index % colors.length]
  }

  return (
    <section className="font-dash text-dash-sm text-ink space-y-6 pb-8">
      {/* 1. Header Section */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          {/*
            ⚠️ "Children", NOT "Students", AND `nav.tsx` ALREADY SAID SO. The
            product calls them children everywhere an adult reads — E1's tile,
            the roster import, the parent tier, the sidebar row that opens this
            screen — and *"a third noun for the same people on the one screen
            that lists them is how a school ends up asking whether the two
            counts mean different things"*. The clash was invisible while the
            chrome bar above said "Dashboard" on every page; it now says what
            the sidebar says, so the two words sat 40px apart.
          */}
          <h1 className="text-dash-xl font-extrabold text-ink">Children</h1>
          <p className="text-dash-xs font-semibold text-muted mt-1">
            Manage and view progress for all children in your school.
          </p>
        </div>
      </div>

      {/* 2. Top Metric Cards (4 Cards Grid) */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        {/* Card 1: Total Children */}
        <div className="bg-surface rounded-2xl border border-line p-5 shadow-card flex items-center justify-between transition-all hover:shadow-card-hover">
          <div>
            {/* ⚠️ THE COMMENT ABOVE ALREADY SAID "Children" AND THE LABEL SAID
                "Students" — the same drift as the heading, on the tile that
                counts the same people. One noun, per `nav.tsx`. */}
            <p className="text-dash-2xs font-bold text-muted uppercase tracking-wider">Total Children</p>
            <h3 className="text-dash-lg font-extrabold text-ink mt-1">{totals.children}</h3>
            <p className="text-dash-2xs font-bold text-muted mt-1">Enrolled across all classes</p>
          </div>
          <div className="h-12 w-12 rounded-2xl bg-indigo-50 text-indigo-600 flex items-center justify-center shrink-0 border border-indigo-100 shadow-2xs">
            <ChildrenIcon size={24} />
          </div>
        </div>

        {/* Card 2: Active Classes */}
        <div className="bg-surface rounded-2xl border border-line p-5 shadow-card flex items-center justify-between transition-all hover:shadow-card-hover">
          <div>
            <p className="text-dash-2xs font-bold text-muted uppercase tracking-wider">Total Classes</p>
            <h3 className="text-dash-lg font-extrabold text-ink mt-1">{totals.classes}</h3>
            <p className="text-dash-2xs font-bold text-muted mt-1">Active class sections</p>
          </div>
          <div className="h-12 w-12 rounded-2xl bg-teal-50 text-teal-600 flex items-center justify-center shrink-0 border border-teal-100 shadow-2xs">
            <SchoolIcon size={24} />
          </div>
        </div>

        {/* Card 3: Never Played */}
        <div className="bg-surface rounded-2xl border border-line p-5 shadow-card flex items-center justify-between transition-all hover:shadow-card-hover">
          <div>
            <p className="text-dash-2xs font-bold text-muted uppercase tracking-wider">Never Played</p>
            <h3 className="text-dash-lg font-extrabold text-ink mt-1">{totals.neverPlayed}</h3>
            <p className="text-dash-2xs font-bold text-muted mt-1">No play history recorded</p>
          </div>
          <div className="h-12 w-12 rounded-2xl bg-amber-50 text-amber-600 flex items-center justify-center shrink-0 border border-amber-100 shadow-2xs">
            <PointIcon size={24} />
          </div>
        </div>

        {/* Card 4: No Parent Linked */}
        <div className="bg-surface rounded-2xl border border-line p-5 shadow-card flex items-center justify-between transition-all hover:shadow-card-hover">
          <div>
            <p className="text-dash-2xs font-bold text-muted uppercase tracking-wider">Unlinked Parents</p>
            <h3 className="text-dash-lg font-extrabold text-ink mt-1">{totals.noParent}</h3>
            <p className="text-dash-2xs font-bold text-muted mt-1">Requires family linking</p>
          </div>
          <div className="h-12 w-12 rounded-2xl bg-rose-50 text-rose-600 flex items-center justify-center shrink-0 border border-rose-100 shadow-2xs">
            <AlertIcon size={24} />
          </div>
        </div>
      </div>

      {/* The form sits above the table it changes, so the new child appears
          directly beneath the box that created them. */}
      {canWrite && showAddForm && (
        <div className="bg-surface rounded-3xl border border-line p-4 sm:p-6 shadow-card">
          <AddStudentPanel
            lang={lang}
            auth={auth}
            classes={classList.data ?? []}
            students={rows}
            onAdded={reload}
          />
        </div>
      )}

      {/* 3. Main Data Table Surface */}
      <div className="bg-surface rounded-3xl border border-line shadow-card overflow-hidden">
        {/*
          Table Toolbar — actions on the first line, filters on the second.

          ⚠️ TWO LINES, BECAUSE THEY ARE TWO KINDS OF CONTROL. "Add" and "Export"
          do something; search and the two selects narrow what is shown. Sharing
          one wrapping row put the primary button wherever the wrap left it.
        */}
        <div className="border-b border-line p-5">
          <div className="flex flex-wrap items-center justify-between gap-3">
            <h2 className="text-dash-md font-extrabold text-ink">
              All Students{' '}
              <span className="font-bold text-muted">
                ({chips.length > 0 ? `${shown.length} of ${rows.length}` : rows.length})
              </span>
            </h2>

            <div className="flex flex-wrap items-center gap-2">
              <FilterAction onClick={download} disabled={shown.length === 0}>
                Export CSV
              </FilterAction>
              {canWrite && (
                <button
                  type="button"
                  onClick={() => setShowAddForm((v) => !v)}
                  className="flex h-10 cursor-pointer items-center gap-2 rounded-xl border border-transparent bg-primary px-4 text-dash-xs font-bold text-white shadow-card transition-colors hover:bg-primary-hover"
                >
                  <span className="font-extrabold">+</span>
                  <span>{showAddForm ? t.addStudentClose : t.addStudent}</span>
                </button>
              )}
            </div>
          </div>

          <div className="mt-4 flex flex-wrap items-center gap-3">
            <FilterSearch
              value={query}
              onChange={(next) => { setQuery(next); setPage(1) }}
              placeholder="Search by name, roll or parent"
              label="Search students"
              className="sm:w-72"
            />

            <FilterSelect
              aria-label="Filter the roster by class"
              value={selectedClassId ?? ''}
              active={selectedClassId !== null}
              onChange={(e) => { setClassId(e.target.value === '' ? null : e.target.value); setPage(1) }}
            >
              <option value="">All classes</option>
              {classes.map((c) => (
                <option key={c.classId} value={c.classId}>
                  {c.label} ({c.count})
                </option>
              ))}
            </FilterSelect>

            <FilterSelect
              aria-label="Filter the roster by how recently a child has played"
              value={activity}
              active={activity !== 'ALL'}
              onChange={(e) => { setActivity(e.target.value as ActivityFilter); setPage(1) }}
            >
              {ACTIVITY_OPTIONS.map((o) => (
                <option key={o.value} value={o.value}>{o.label}</option>
              ))}
            </FilterSelect>
          </div>

          {chips.length > 0 && (
            <div className="mt-3">
              <ActiveFilters chips={chips} onClearAll={clearFilters} />
            </div>
          )}
        </div>

        {/* Table Content */}
        <DataSurface
          state={resource.state}
          skeletonRows={6}
          emptyMessage={t.noteNoChildren}
          errorMessage={describeAdminError(resource.error, lang)}
          errorLastWorked={
            resource.lastLoadedAt ? t.lastWorked(resource.lastLoadedAt.toLocaleTimeString()) : t.neverWorked
          }
          errorRetryLabel={t.retry}
          onRetry={resource.retry}
        >
          <div className="overflow-x-auto">
            <table className="w-full text-left border-collapse">
              <thead>
                <tr className="border-b border-line bg-canvas/50 text-dash-2xs font-extrabold text-muted uppercase tracking-wider">
                  <th className="py-3.5 px-5">Student ↕</th>
                  <th className="py-3.5 px-4">Class & Band</th>
                  <th className="py-3.5 px-4">Parent / Guardian</th>
                  <th className="py-3.5 px-4">Last Activity ↕</th>
                  <th className="py-3.5 px-5 text-right">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-line text-dash-xs font-semibold text-ink">
                {paginatedRows.length === 0 ? (
                  <tr>
                    <td colSpan={5} className="px-5">
                      <NoMatches noun="students" onClear={chips.length > 0 ? clearFilters : undefined} />
                    </td>
                  </tr>
                ) : (
                  paginatedRows.map((student, idx) => {
                    const badgeClass = getBadgeColor(idx)
                    const initials = getInitials(student.name)
                    const actState = activityState(student, now)

                    return (
                      <tr
                        key={student.playerId}
                        className="hover:bg-canvas/40 transition-colors cursor-pointer"
                        onClick={() => onOpenChild(student)}
                      >
                        {/* Student Info */}
                        <td className="py-4 px-5">
                          <div className="flex items-center gap-3">
                            <div className={`h-10 w-10 rounded-full font-extrabold text-dash-xs flex items-center justify-center shrink-0 border ${badgeClass}`}>
                              {initials}
                            </div>
                            <div>
                              <div className="font-extrabold text-ink">{student.name}</div>
                              <div className="text-dash-2xs text-muted font-normal mt-0.5">
                                Roll No: {student.rollNumber ?? 'N/A'}
                              </div>
                            </div>
                          </div>
                        </td>

                        {/* Class & Band */}
                        <td className="py-4 px-4">
                          <div className="flex items-center gap-2">
                            <span className="inline-block px-2.5 py-0.5 rounded-md bg-purple-50 text-purple-700 text-dash-2xs font-bold border border-purple-100">
                              {student.className}
                            </span>
                            <span className="inline-block px-2.5 py-0.5 rounded-md bg-blue-50 text-blue-700 text-dash-2xs font-bold border border-blue-100">
                              {BAND_LABEL[student.ageBand]?.[lang] ?? student.ageBand}
                            </span>
                          </div>
                        </td>

                        {/* Parent / Guardian */}
                        <td className="py-4 px-4">
                          {student.parentName ? (
                            <div className="space-y-0.5">
                              <div className="font-bold text-ink flex items-center gap-1.5">
                                <ChildrenIcon className="text-muted" />
                                <span>{student.parentName}</span>
                              </div>
                              {student.parentPhone && (
                                <div className="text-dash-2xs text-muted font-normal">
                                  {student.parentPhone}
                                </div>
                              )}
                            </div>
                          ) : (
                            <span className="inline-block px-2.5 py-0.5 rounded-full bg-rose-50 text-rose-700 text-dash-2xs font-bold border border-rose-100">
                              Unlinked
                            </span>
                          )}
                        </td>

                        {/* Last Activity */}
                        <td className="py-4 px-4">
                          <LastPlayed state={actState} row={student} lang={lang} />
                        </td>

                        {/* Actions */}
                        <td className="py-4 px-5 text-right">
                          <button
                            type="button"
                            onClick={(e) => {
                              e.stopPropagation()
                              onOpenChild(student)
                            }}
                            className="px-3 py-1.5 rounded-xl border border-line bg-canvas hover:bg-surface text-dash-2xs font-extrabold text-primary hover:text-primary-hover transition-colors cursor-pointer"
                          >
                            View Report →
                          </button>
                        </td>
                      </tr>
                    )
                  })
                )}
              </tbody>
            </table>
          </div>
        </DataSurface>

        {/* Table Pagination Footer */}
        <div className="p-4 border-t border-line flex flex-col sm:flex-row sm:items-center justify-between gap-4 bg-canvas/30">
          <div className="text-dash-xs font-semibold text-muted">
            Showing {shown.length === 0 ? 0 : (page - 1) * pageSize + 1} to{' '}
            {Math.min(page * pageSize, shown.length)} of {shown.length} students
          </div>

          <div className="flex items-center gap-3">
            {/* Pagination buttons */}
            <div className="flex items-center gap-1">
              <button
                type="button"
                disabled={page <= 1}
                onClick={() => setPage((p) => p - 1)}
                className="h-8 w-8 rounded-lg border border-line bg-surface text-ink hover:bg-canvas disabled:opacity-40 disabled:cursor-not-allowed font-bold transition-colors cursor-pointer"
              >
                ‹
              </button>
              {Array.from({ length: totalPages }, (_, i) => i + 1).map((p) => (
                <button
                  key={p}
                  type="button"
                  onClick={() => setPage(p)}
                  className={`h-8 w-8 rounded-lg font-extrabold transition-colors cursor-pointer ${
                    p === page
                      ? 'bg-primary text-white shadow-2xs'
                      : 'border border-line bg-surface text-ink hover:bg-canvas'
                  }`}
                >
                  {p}
                </button>
              ))}
              <button
                type="button"
                disabled={page >= totalPages}
                onClick={() => setPage((p) => p + 1)}
                className="h-8 w-8 rounded-lg border border-line bg-surface text-ink hover:bg-canvas disabled:opacity-40 disabled:cursor-not-allowed font-bold transition-colors cursor-pointer"
              >
                ›
              </button>
            </div>

            {/* Page Size Selector */}
            <select
              value={pageSize}
              onChange={(e) => {
                setPageSize(Number(e.target.value))
                setPage(1)
              }}
              className="rounded-lg border border-line bg-surface px-2.5 py-1 text-dash-xs font-bold text-ink focus:border-primary focus:outline-none cursor-pointer"
            >
              <option value={10}>10 / page</option>
              <option value={20}>20 / page</option>
              <option value={50}>50 / page</option>
            </select>
          </div>
        </div>
      </div>
    </section>
  )
}

/**
 * 🔴 §12 — ICON **AND** COLOUR **AND** TEXT, ALL THREE. "Never" is the state an
 * admin can act on, so it is the one that carries a glyph of its own; a child
 * who played last term is a fact rather than a fault, which is why nothing here
 * is `danger`. `AttendanceScreen` refuses red on a child's count for the same
 * reason: this is a fact about a term, not an alarm about a family.
 */
/**
 * E7 — put one child on a register.
 *
 * 🔴 THE PARENT FIELDS ARE HERE AND THEY ARE NOT DECORATION. D5 draws
 * *"progress reports cannot be sent until a parent is linked"*, and the moment a
 * child is enrolled is the moment the office has the family's number in front of
 * them. Leaving them out would create the exact row that screen complains about.
 *
 * ⚠️ A NUMBER THAT ALREADY HAS A PARENT ACCOUNT IS LINKED, NOT DUPLICATED —
 * that is how a family with two children ends up with one login — and a number
 * belonging to STAFF is refused outright by the server, because linking it would
 * hand a colleague a parent's view of a child.
 */
function AddStudentPanel({
  lang,
  auth,
  classes,
  students,
  onAdded,
}: {
  lang: Lang
  auth?: StaffAuth
  classes: readonly ClassSummary[]
  /** Already loaded by the page — the roll-number check costs no request. */
  students: readonly StudentRow[]
  onAdded: () => void
}) {
  const t = ADMIN_COPY[lang]
  const [classId, setClassId] = useState('')
  const [name, setName] = useState('')
  const [roll, setRoll] = useState('')
  const [parentName, setParentName] = useState('')
  const [parentPhone, setParentPhone] = useState('')
  const [busy, setBusy] = useState(false)
  const [failed, setFailed] = useState<string | null>(null)
  const [added, setAdded] = useState<{ row: AddedStudent; className: string } | null>(null)

  const live = useMemo(() => enrollableClasses(classes), [classes])

  /*
   * 🔴 THE CLASH IS FOUND BEFORE THE REQUEST AND NAMES THE CHILD. The server
   * refuses a duplicate roll anyway; what this buys is that an admin is told
   * *"roll 03 is Meera's"* while the box is under their cursor, instead of
   * "that did not save" after a round trip.
   */
  const clash = useMemo(
    () => (classId === '' ? null : rollNumberClash(students, classId, roll)),
    [students, classId, roll],
  )

  /* A name with no number links nobody — see `canAddStudent`. */
  const parentNameOrphaned = parentName.trim() !== '' && parentPhone.trim() === ''

  /* ⚠️ ONLY A *CERTAIN* CLASH BLOCKS. An equivalent-looking roll is one the
     server would accept, and refusing it here would be this form inventing a
     rule — see `RollClash.certain`. */
  const ready =
    canAddStudent({ classId, name, parentName, parentPhone }) && clash?.certain !== true

  const submit = async () => {
    setBusy(true)
    setFailed(null)
    try {
      const row = await addStudent(
        {
          classId,
          name: name.trim(),
          rollNumber: roll.trim() === '' ? null : roll.trim(),
          /* ⚠️ THE PARENT FIELDS ARE OMITTED ENTIRELY WHEN NO NUMBER WAS TYPED.
             Sending empty strings would make the server read this as an attempt
             to link a family and refuse the format. */
          ...(parentPhone.trim() !== ''
            ? {
                parentName: parentName.trim() === '' ? null : parentName.trim(),
                parentPhone: parentPhone.trim(),
              }
            : {}),
        },
        auth,
      )
      setAdded({ row, className: live.find((c) => c.id === classId)?.name ?? '' })
      setName('')
      setRoll('')
      setParentName('')
      setParentPhone('')
      onAdded()
    } catch (err) {
      /*
       * 🔴 THREE DIFFERENT 409s ON ONE FORM, TOLD APART BY THE SERVER'S CODE.
       * A roll number in use, a staff phone number, and a plan with no seats
       * left need three different actions — and one of them cannot be fixed on
       * this form at all. `client.ts` reads the slug and never the sentence;
       * `StudentService.addStudent` and `SeatsService` send them.
       */
      const e = err as { status?: number; code?: string | null } | null
      if (e?.code === 'SEATS_FULL') setFailed(t.addStudentSeatsFull)
      else if (e?.code === 'PHONE_IS_STAFF') setFailed(t.addStudentPhoneIsStaff)
      else if (e?.status === 400) setFailed(t.addStudentBadRequest)
      else setFailed(t.addStudentFailed)
    } finally {
      setBusy(false)
    }
  }

  /*
   * A receipt, and it says what happened to the FAMILY as well as to the child.
   * "Saved" would hide the difference between a parent account created, an
   * existing one linked, and no number at all — three different next steps.
   */
  if (added !== null) {
    return (
      <div className="max-w-prose rounded-lg border border-line bg-surface p-4">
        <h3 className="flex items-center gap-2 text-dash-sm font-extrabold text-ink">
          <CheckIcon size={16} />
          {t.studentAdded(added.row.name, added.className)}
        </h3>
        <p className="mt-1 text-dash-xs text-muted">
          {added.row.parentCreated
            ? t.studentAddedParentCreated
            : added.row.parentLinked
              ? t.studentAddedParentLinked
              : t.studentAddedNoParent}
        </p>
        <div className="mt-4">
          <DashButton variant="secondary" onClick={() => setAdded(null)}>
            {t.addAnotherStudent}
          </DashButton>
        </div>
      </div>
    )
  }

  return (
    <Panel>
      <form
        onSubmit={(e) => {
          e.preventDefault()
          void submit()
        }}
      >
        <h2 className="text-dash-md font-extrabold text-ink">{t.addStudentTitle}</h2>
        <p className="mt-1 max-w-prose text-dash-xs text-muted">{t.addStudentHint}</p>

        <div className="mt-4 grid max-w-xl gap-4 sm:grid-cols-2">
          <Field label={t.studentClass} htmlFor="student-class" width="full">
            <Select id="student-class" value={classId} onChange={(e) => setClassId(e.target.value)}>
              <option value="">{t.studentClassChoose}</option>
              {live.map((c) => (
                <option key={c.id} value={c.id}>
                  {c.name}
                </option>
              ))}
            </Select>
          </Field>
          <Field label={t.studentName} htmlFor="student-name" width="full">
            <Input
              id="student-name"
              value={name}
              onChange={(e) => setName(e.target.value)}
              maxLength={80}
            />
          </Field>
          <Field label={t.studentRoll} htmlFor="student-roll" width="full">
            <Input
              id="student-roll"
              value={roll}
              onChange={(e) => setRoll(e.target.value)}
              maxLength={16}
            />
          </Field>
          <Field label={t.studentParentPhone} htmlFor="student-parent-phone" width="full">
            <Input
              id="student-parent-phone"
              inputMode="tel"
              autoComplete="off"
              value={parentPhone}
              onChange={(e) => setParentPhone(e.target.value)}
              maxLength={20}
            />
          </Field>
          <Field label={t.studentParentName} htmlFor="student-parent-name" width="full">
            <Input
              id="student-parent-name"
              value={parentName}
              onChange={(e) => setParentName(e.target.value)}
              maxLength={80}
            />
          </Field>
        </div>

        <p className="mt-3 max-w-prose text-dash-xs text-muted">{t.studentRollHint}</p>
        <p className="mt-1 max-w-prose text-dash-xs text-muted">{t.studentParentHint}</p>

        {clash !== null && (
          <div className="mt-3 max-w-prose">
            <Note tone="warning" icon={<InfoIcon size={16} />}>
              {clash.certain
                ? t.rollTakenBy(clash.student.name, roll.trim())
                : t.rollLooksLike(
                    clash.student.name,
                    roll.trim(),
                    (clash.student.rollNumber ?? '').trim(),
                  )}
            </Note>
          </div>
        )}

        {parentNameOrphaned && (
          <div className="mt-3 max-w-prose">
            <Note tone="warning" icon={<InfoIcon size={16} />}>
              {t.parentNameNeedsPhone}
            </Note>
          </div>
        )}

        <div className="mt-4">
          <DashButton type="submit" disabled={busy || !ready}>
            {busy ? t.addStudentSaving : t.addStudentAction}
          </DashButton>
        </div>

        {failed !== null && (
          <div className="mt-4">
            <Note tone="warning" icon={<AlertIcon size={16} />}>
              {failed}
            </Note>
          </div>
        )}
      </form>
    </Panel>
  )
}

function LastPlayed({
  state,
  row,
  lang,
}: {
  state: ActivityState
  row: StudentRow
  lang: Lang
}) {
  const t = ADMIN_COPY[lang]

  if (state === 'NEVER') {
    return (
      <StatusPill tone="neutral" icon={<InfoIcon size={14} />}>
        {t.neverPlayedShort}
      </StatusPill>
    )
  }

  /* The date itself, because "this week" without it is an answer a head teacher
     has to take on trust. The pill says which side of the line it falls on. */
  const on = row.lastPlayed === null ? t.dash : new Date(row.lastPlayed).toLocaleDateString()

  return state === 'RECENT' ? (
    <StatusPill tone="success" icon={<CheckIcon size={14} />}>
      {on}
    </StatusPill>
  ) : (
    <StatusPill tone="warning" icon={<AlertIcon size={14} />}>
      {on}
    </StatusPill>
  )
}


